/**
 * AdminUpdates — combined view for the two campaign-update admin flows.
 *
 * Split into two tabs so the ops workload stays organized:
 *
 *   1. Moderation  — student-authored updates that a visitor has reported
 *                    for a possible platform-guideline violation. Admin
 *                    decides: keep visible, or remove.
 *
 *   2. Compose     — admin composer. Pick any active campaign and post an
 *                    update as My Rich Aunt. Useful for confirming a grant
 *                    disbursement, verifying an enrollment change, or
 *                    adding context donors would want to see.
 *
 * All state is client-local for the prototype (localStorage via
 * lib/campaignUpdates.js). Backend swap is a one-file exchange.
 */

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Flag, MessageSquareText, Send, Check, Trash2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { SectionHeader, AdminButton, EmptyState, StatusBadge } from "./_ui";
import {
  getReportedUpdates,
  getAllUpdates,
  keepUpdate,
  removeUpdate,
  postUpdate,
  subscribeUpdates,
} from "../../lib/campaignUpdates";
import { CAMPAIGN_ROSTER } from "../../data/adminMock";

const relTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (secs < 60)             return `${secs}s ago`;
  if (secs < 3600)           return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400)          return `${Math.round(secs / 3600)}h ago`;
  if (secs < 86400 * 30)     return `${Math.round(secs / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const TAB_META = {
  moderation: { label: "Moderation queue", eyebrow: "Review reported updates" },
  compose:    { label: "Post an update",   eyebrow: "Speak as My Rich Aunt" },
};

export const AdminUpdates = () => {
  const [tab, setTab] = useState("moderation");
  const [reported, setReported] = useState(() => getReportedUpdates());
  const [all, setAll] = useState(() => getAllUpdates());
  const [moderatorNote, setModeratorNote] = useState({}); // keyed by update id

  useEffect(
    () =>
      subscribeUpdates(() => {
        setReported(getReportedUpdates());
        setAll(getAllUpdates());
      }),
    [],
  );

  return (
    <div className="space-y-8">
      <SectionHeader
        eyebrow="Campaign updates"
        title="Moderation and admin voice."
        description="Review student-posted updates that visitors have flagged, and post updates to any campaign as My Rich Aunt."
      />

      {/* Tabs */}
      <div className="flex items-center gap-2" role="tablist">
        {Object.entries(TAB_META).map(([key, meta]) => {
          const isActive = tab === key;
          const badgeCount = key === "moderation" ? reported.length : 0;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`updates-tab-${key}`}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium transition ${
                isActive
                  ? "bg-ink text-sand"
                  : "bg-sand text-ink ring-1 ring-inset ring-ink/15 hover:ring-ink/40"
              }`}
            >
              {meta.label}
              {badgeCount > 0 && (
                <span
                  className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${
                    isActive ? "bg-tangerine text-sand" : "bg-tangerine/20 text-tangerine"
                  }`}
                >
                  {badgeCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "moderation" ? (
        <ModerationPanel
          reported={reported}
          moderatorNote={moderatorNote}
          setModeratorNote={setModeratorNote}
        />
      ) : (
        <ComposePanel />
      )}

      {/* Recent history block — always visible for context */}
      <RecentActivity all={all} />
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Moderation panel                                                           */
/* -------------------------------------------------------------------------- */

const ModerationPanel = ({ reported, moderatorNote, setModeratorNote }) => {
  const onKeep = (id) => {
    keepUpdate(id, moderatorNote[id] || "");
    toast.success("Update kept live", {
      description: "The report is closed. Student is not notified.",
    });
  };
  const onRemove = (id) => {
    removeUpdate(id, moderatorNote[id] || "");
    toast.message("Update removed", {
      description: "Hidden from the public feed. Retained in the record for audit.",
    });
  };

  if (reported.length === 0) {
    return (
      <EmptyState
        title="Nothing to moderate"
        description="No student updates have been reported by visitors. When one is flagged, the case will appear here."
      />
    );
  }

  return (
    <ul className="space-y-5" data-testid="updates-moderation-list">
      {reported.map((u) => {
        const campaign = CAMPAIGN_ROSTER[u.campaignId];
        return (
          <li
            key={u.id}
            data-testid={`moderation-row-${u.id}`}
            className="overflow-hidden rounded-lg border border-scarlet/25 bg-scarlet/[0.04]"
          >
            <div className="flex items-center justify-between gap-3 border-b border-scarlet/20 bg-scarlet/[0.06] px-5 py-3">
              <div className="flex items-center gap-2">
                <Flag className="h-3.5 w-3.5 text-scarlet" strokeWidth={2} />
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-scarlet">
                  Reported by a visitor
                </span>
                <span className="text-[11px] text-ink/60">· flagged {relTime(u.reportedAt)}</span>
              </div>
              {u.reportReason && (
                <span className="text-[11px] italic text-ink/60">"{u.reportReason}"</span>
              )}
            </div>

            <div className="grid grid-cols-1 gap-5 px-5 py-5 lg:grid-cols-12">
              {/* Left: the update in question */}
              <div className="lg:col-span-7">
                <div className="flex items-center gap-2 text-sm">
                  <MessageSquareText className="h-4 w-4 text-tangerine" strokeWidth={1.75} />
                  <span className="font-medium text-ink">{u.authorName}</span>
                  <span className="text-xs text-ink/60">· Student · posted {relTime(u.postedAt)}</span>
                </div>
                <p className="mt-3 rounded-md border border-ink/10 bg-beige/60 p-4 text-sm leading-relaxed text-ink">
                  {u.body}
                </p>

                {campaign && (
                  <Link
                    to={`/demo/campaign/${u.campaignId}`}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-ink hover:text-tangerine"
                    data-testid={`moderation-view-${u.id}`}
                  >
                    View campaign · {campaign.name}
                    <ExternalLink className="h-3 w-3" strokeWidth={2} />
                  </Link>
                )}
              </div>

              {/* Right: decision */}
              <div className="lg:col-span-5">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
                  Decision note (optional)
                </label>
                <textarea
                  rows={3}
                  value={moderatorNote[u.id] || ""}
                  onChange={(e) =>
                    setModeratorNote((prev) => ({ ...prev, [u.id]: e.target.value }))
                  }
                  placeholder="Notes captured for the audit trail..."
                  className="mt-2 w-full rounded-md border border-ink/15 bg-sand p-3 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-ink/20"
                  data-testid={`moderation-note-${u.id}`}
                />
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <AdminButton
                    variant="approve"
                    size="sm"
                    onClick={() => onKeep(u.id)}
                    testid={`moderation-keep-${u.id}`}
                  >
                    <Check className="h-3 w-3" strokeWidth={2} /> Keep update
                  </AdminButton>
                  <AdminButton
                    variant="reject"
                    size="sm"
                    onClick={() => onRemove(u.id)}
                    testid={`moderation-remove-${u.id}`}
                  >
                    <Trash2 className="h-3 w-3" strokeWidth={2} /> Remove
                  </AdminButton>
                </div>
                <p className="mt-2 text-[11px] italic text-ink/50">
                  Every decision is logged with your name and timestamp. Report stays on the record either way.
                </p>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

/* -------------------------------------------------------------------------- */
/* Compose panel — post an update as My Rich Aunt                              */
/* -------------------------------------------------------------------------- */

const ComposePanel = () => {
  const [campaignId, setCampaignId] = useState(CAMPAIGN_ROSTER[0]?.idx ?? 0);
  const [body, setBody] = useState("");

  const roster = useMemo(
    () => CAMPAIGN_ROSTER.filter((c) => c.status !== "closed"),
    [],
  );

  const onPost = () => {
    const trimmed = body.trim();
    if (trimmed.length === 0) {
      toast.error("Add a message before posting.");
      return;
    }
    postUpdate({
      campaignId,
      author: "admin",
      authorName: "My Rich Aunt",
      body: trimmed,
    });
    const campaign = CAMPAIGN_ROSTER[campaignId];
    toast.success("Update posted", {
      description: `Now visible on ${campaign?.name || "the campaign"}'s public page.`,
    });
    setBody("");
  };

  return (
    <div className="rounded-lg border border-cobalt/25 bg-cobalt/[0.04] p-6" data-testid="updates-compose-panel">
      <div className="flex items-center gap-2">
        <MessageSquareText className="h-4 w-4 text-cobalt" strokeWidth={1.75} />
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-cobalt">
          Speak as My Rich Aunt
        </span>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-olive">
        Confirm a grant disbursement, share verified news about a student, or add context donors would want to see.
        Admin updates carry a small "From My Rich Aunt" badge on the public feed and cannot be reported by visitors.
      </p>

      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
            Campaign
          </label>
          <select
            value={campaignId}
            onChange={(e) => setCampaignId(Number(e.target.value))}
            data-testid="updates-compose-campaign"
            className="mt-2 w-full rounded-md border border-ink/15 bg-sand px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-cobalt/40"
          >
            {roster.map((c) => (
              <option key={c.idx} value={c.idx}>
                {c.name} · {c.school}
              </option>
            ))}
          </select>
        </div>
        <div className="text-sm text-olive md:pt-6">
          Posting as{" "}
          <span className="font-medium text-ink">My Rich Aunt</span>. Visible immediately on the public feed.
        </div>
      </div>

      <label className="mt-5 block text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
        Update
      </label>
      <textarea
        rows={5}
        value={body}
        maxLength={1000}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Example: This week's grant of $450 has been sent to Maya. Enrollment confirmed for Spring 2026."
        className="mt-2 w-full rounded-md border border-ink/15 bg-sand p-3 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-cobalt/40"
        data-testid="updates-compose-body"
      />
      <div className="mt-2 flex items-center justify-between text-[11px] text-olive">
        <span>{1000 - body.length} characters left</span>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <AdminButton onClick={onPost} testid="updates-compose-post">
          <Send className="h-3.5 w-3.5" strokeWidth={2} /> Post update
        </AdminButton>
        <button
          type="button"
          onClick={() => setBody("")}
          className="text-xs text-olive hover:text-ink"
          data-testid="updates-compose-clear"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Recent activity                                                            */
/* -------------------------------------------------------------------------- */

const RecentActivity = ({ all }) => {
  const recent = all.slice(0, 6);
  if (recent.length === 0) return null;
  return (
    <div>
      <h4 className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
        Recent activity
      </h4>
      <ul className="mt-3 divide-y divide-ink/10 overflow-hidden rounded-lg border border-ink/10 bg-sand">
        {recent.map((u) => {
          const campaign = CAMPAIGN_ROSTER[u.campaignId];
          return (
            <li
              key={u.id}
              className="flex items-center gap-4 px-5 py-3"
              data-testid={`updates-history-${u.id}`}
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-tangerine/15">
                {u.author === "admin" ? (
                  <MessageSquareText className="h-3.5 w-3.5 text-cobalt" strokeWidth={2} />
                ) : (
                  <MessageSquareText className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-ink">
                  <span className="font-medium">{u.authorName}</span>
                  <span className="text-ink/60">
                    {" "}on{" "}
                    {campaign ? campaign.name : "campaign"}
                    {" "}· {relTime(u.postedAt)}
                  </span>
                </p>
                <p className="mt-0.5 truncate text-xs text-ink/60">{u.body}</p>
              </div>
              <StatusBadge status={u.state === "visible" ? "live" : u.state === "reported" ? "flagged" : "closed"} />
            </li>
          );
        })}
      </ul>
    </div>
  );
};
